import axios, { AxiosInstance } from 'axios';
import { api } from './api';
import type { ReviewCycleState, ReviewOutcome, ReviewRating, ReviewSeat } from '../contracts';

const apiBaseURL = import.meta.env.VITE_API_URL ?? '/api';

// Reviewers never hold an operator token: their session is an httpOnly cookie that
// only /api/review/reviewer/* accepts.
export const reviewerApi = axios.create({
  baseURL: `${apiBaseURL}/review/reviewer`,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

reviewerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !String(error.config?.url ?? '').endsWith('/me')) {
      window.location.href = '/review';
    }
    return Promise.reject(error);
  },
);

export const reviewAuthApi = axios.create({
  baseURL: `${apiBaseURL}/review/auth`,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export const reviewSignInUrl = `${apiBaseURL}/review/auth/login`;

export const staffBase = '/review/staff';

/** Fetch an attachment through the platform (policy-checked, logged) and save it. */
export async function downloadAttachment(client: AxiosInstance, url: string, label: string) {
  const res = await client.get(url, { responseType: 'blob' });
  const disposition = String(res.headers['content-disposition'] ?? '');
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? label;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}
export { api as staffApi };

export const STATES = [
  'draft',
  'open',
  'intake_closed',
  'eligibility',
  'assignment',
  'review',
  'calibration',
  'deliberation',
  'decided',
  'archived',
] as const satisfies readonly ReviewCycleState[];
export type CycleState = ReviewCycleState;

export type Seat = ReviewSeat;
export type Rating = ReviewRating;
export type Outcome = ReviewOutcome;

export interface CycleSummary {
  id: string;
  year: number;
  name: string;
  state: CycleState;
  stateChangedAt: string | null;
  applications: number;
  eligible: number;
  isProgramManager: boolean;
}

export interface Criterion {
  key: string;
  section: string;
  label: string;
  scale: 'lmh' | 'count_lmh' | 'score' | 'text';
  guidance?: string;
  hint?: string;
  prefill?: string;
  prefillValue?: number | null;
  min?: number;
  max?: number;
  optional?: boolean;
  evidence?: string[];
}

export interface Rubric {
  version: string;
  criteria: Criterion[];
}

export interface Settings {
  reviewersPerApplication: number;
  discrepancyThreshold: number;
  selfIdDuringScoring: boolean;
  reviewerGroups: string[];
  acceptedGroups: string[];
  extraQuestionTiers: Record<string, string>;
  retention: {
    unsuccessfulYears: number;
    fellowshipYears: number;
    acceptedYearsAfterFellowship: number;
    auditYears: number;
  };
}

export interface CommitteeMember {
  id: string;
  seat: Seat;
  seatLabel: string | null;
  userId: string | null;
  adminUserId: string | null;
  name: string | null;
  email: string | null;
  otherEmails: string[];
  activeAssignments: number;
}

export interface EditionRef {
  id: string;
  formId: string;
  label: string;
  year: number | null;
  status?: string;
}

export interface CycleDetail {
  id: string;
  year: number;
  name: string;
  state: CycleState;
  stateChangedAt: string | null;
  settings: Settings;
  rubric: Rubric;
  eligibilityRules: { version: string; rules: Record<string, unknown>[] };
  editions: EditionRef[];
  availableEditions: EditionRef[];
  committee: CommitteeMember[];
  applicationCounts: Record<string, number>;
  reviewsStarted: number;
  next: CycleState | null;
  previous: CycleState | null;
  can: Record<string, boolean>;
}

export interface Answer {
  key: string;
  label: string;
  type: string;
  value: unknown;
}

export interface Extra {
  id: string;
  label: string;
  value: unknown;
}

export interface Score {
  rating: Rating | null;
  count: number | null;
  score: number | null;
  text: string | null;
}

export interface OwnReview {
  id: string;
  state: 'draft' | 'submitted' | 'reopened' | 'withdrawn';
  attestedAt: string;
  submittedAt: string | null;
  reopenedAt: string | null;
  reopenReason: string | null;
  rubricVersion: string | null;
  scores: Record<string, Score>;
}

export interface OtherReview {
  id: string;
  reviewer: string | null;
  reviewerUserId: string;
  state: string;
  submittedAt: string | null;
  scores: Record<string, Score> | null;
}

export interface Summary {
  submitted: number;
  meanPriority: number | null;
  priorities: number[];
  spread: number | null;
  flagged: boolean;
}

export interface Decision {
  id: string;
  outcome: Outcome;
  rationale: string | null;
  conditionText: string | null;
  conditionDueDate: string | null;
  conditionOwner: string | null;
  conditionStatus: 'pending' | 'met' | 'not_met' | null;
  amendsDecisionId: string | null;
  recordedAt: string;
  groupSync: { group: string | null; status: string }[] | null;
}

export interface Identity {
  name: string | null;
  email: string | null;
  institution: string | null;
  careerStage: string | null;
  degreeLevel: string | null;
  discipline: string | null;
  disciplineOther: string | null;
  region: string | null;
  supervisorName: string | null;
  supervisorIsInvestigator: boolean | null;
  preferredLanguage: string | null;
  pronouns: string | null;
  submittedAt: string | null;
  late: boolean;
}

export interface ApplicationView {
  id: string;
  identity?: Identity;
  substance?: {
    answers: Answer[];
    extra: Extra[];
    leave: { tookLeave: boolean | null; detail: unknown };
    supervisorEmail: string | null;
  };
  rubric?: Rubric;
  eligibility?: {
    status: string;
    flags: { rule: string; outcome: string; message: string }[];
    version: string | null;
    note: string | null;
  };
  contact?: Extra[];
  selfId?: { answers: Answer[]; extra: Extra[] };
  review?: OwnReview | null;
  otherReviews?: OtherReview[];
  summary?: Summary;
  decision?: Decision | null;
  access?: {
    attest: boolean;
    edit: boolean;
    submit: boolean;
    declareConflict: boolean;
    recordDecision: boolean;
  };
  attachments?: { fieldId: string; label: string }[];
  cycleState?: CycleState;
  assignmentId?: string;
  assignments?: {
    id: string;
    state: string;
    purpose: string;
    reviewerUserId: string;
    reviewer: string | null;
    endedReason: string | null;
  }[];
  withdrawn?: boolean;
  reduced?: boolean;
}

export interface StaffApplication {
  id: string;
  name: string | null;
  email: string | null;
  member: boolean;
  careerStage: string | null;
  discipline: string | null;
  region: string | null;
  institution: string | null;
  submittedAt: string | null;
  late: boolean;
  eligibilityStatus: 'pending' | 'eligible' | 'flagged' | 'ineligible';
  eligibilityFlags: { rule: string; outcome: string; message: string }[];
  eligibilityNote: string | null;
  eligibilityResolved: boolean;
  withdrawn: boolean;
  reduced: boolean;
  outcome: Outcome | null;
  replacedById: string | null;
  replacedAt: string | null;
  replacedByChoice: boolean;
  waitingOnId: string | null;
}

export interface Progress {
  perApplication: number;
  reviewers: {
    userId: string;
    name: string | null;
    proposed: number;
    assigned: number;
    attested: number;
    submitted: number;
    conflicted: number;
    notified: number;
  }[];
  applications: {
    id: string;
    applicantName: string | null;
    careerStage: string | null;
    discipline: string | null;
    region: string | null;
    eligibilityStatus: string;
    withdrawn: boolean;
    needsReviewers: number;
    assignments: {
      id: string;
      reviewerUserId: string;
      reviewer: string | null;
      state: string;
      purpose: string;
      review: string | null;
      softConflict: boolean;
    }[];
  }[];
}

export interface Conflict {
  id: string;
  applicationId: string;
  applicant: string | null;
  member: string | null;
  userId: string;
  type: string;
  typeLabel: string;
  level: 'hard' | 'soft';
  effective: 'hard' | 'soft' | null;
  source: 'auto' | 'declared';
  detail: string | null;
  status: 'open' | 'allowed' | 'treated_as_hard' | 'dismissed';
  decidedAt: string | null;
  decisionReason: string | null;
  canRule: boolean;
  canDismiss: boolean;
}

export interface CalibrationRow {
  id: string;
  recused?: true;
  name?: string | null;
  careerStage?: string | null;
  discipline?: string | null;
  region?: string | null;
  assigned?: number;
  hasThird?: boolean;
  summary?: Summary;
  reviews?: {
    id: string;
    reviewer: string | null;
    state: string;
    scores: Record<string, Score> | null;
    canReopen: boolean;
  }[];
}

export interface Calibration {
  state: CycleState;
  threshold: number;
  criteria: Criterion[];
  canAddThird: boolean;
  rows: CalibrationRow[];
  recused: number;
}

export interface DeliberationRow {
  id: string;
  name: string | null;
  careerStage: string | null;
  discipline: string | null;
  region: string | null;
  institution: string | null;
  summary: Summary;
  canDecide: boolean;
  selfId?: { key: string; label: string; value: unknown }[];
  decision?: Decision | null;
  recusals?: (string | null)[];
  rank: number | null;
  stageRank: number | null;
}

export interface Equity {
  applicants: number;
  accepted: number;
  dimensions: { key: string; label: string; values: { value: string; applicants: number; accepted: number }[] }[];
}

export interface Deliberation {
  state: CycleState;
  threshold: number;
  rows: DeliberationRow[];
  recused: number;
  equity: Equity | null;
  perApplicantSelfId: boolean;
}

export interface QueueItem {
  assignmentId: string;
  applicationId: string;
  applicantName: string | null;
  careerStage: string;
  discipline: string | null;
  region: string | null;
  institution: string | null;
  purpose: 'primary' | 'third';
  status: 'needs_attestation' | 'draft' | 'submitted' | 'reopened' | 'withdrawn';
  canAttest: boolean;
  canEdit: boolean;
}

export interface Queue {
  cycle: { id: string; year: number; name: string; state: CycleState };
  seats: Seat[];
  groups: { careerStage: string; items: QueueItem[] }[];
  conflictsDeclared?: number;
  conflictTypes?: { type: string; label: string; level: 'hard' | 'soft' }[];
}

export interface ReviewerMe {
  userId: string;
  email: string;
  name: string;
  cycles: { id: string; year: number; name: string; state: CycleState; seats: Seat[] }[];
}

export interface AuditRow {
  id: number;
  at: string;
  actor: string;
  actorType: string;
  action: string;
  phase: string | null;
  applicant: string | null;
  reason: string | null;
  detail: Record<string, unknown> | null;
}

export const stateLabel = (s: string | null | undefined) =>
  s ? s.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—';

export const OUTCOME_LABELS: Record<Outcome, string> = {
  accepted: 'Accepted',
  transferred: 'Transferred to Associate Fellow',
  conditional: 'Conditional',
  ineligible: 'Ineligible',
};

export const SEAT_LABELS: Record<Seat, string> = {
  program_manager: 'Program Manager',
  chair: 'Chair',
  co_chair: 'Co-chair',
  reviewer: 'Reviewer',
  observer: 'Observer',
};

export function errorMessage(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;
}

export function isUnauthorized(err: unknown) {
  return (err as { response?: { status?: number } })?.response?.status === 401;
}

export function showValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.length ? v.map(showValue).join(', ') : '—';
  return String(v);
}

export function downloadCsv(data: Blob, fallbackName: string, disposition?: string) {
  const name = /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
